export type ThemeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
export type ThemeControlSize = 'xs' | 'sm' | 'md' | 'lg';
export type ThemeSegment = 'first' | 'middle' | 'last';

export const themeToneClasses: Record<ThemeTone, string> = {
  success: 'border-success-500/25 bg-success-50 text-success-500',
  warning: 'border-warning-500/25 bg-warning-50 text-warning-500',
  danger: 'border-error-500/25 bg-error-50 text-error-500',
  info: 'border-info-500/25 bg-info-50 text-info-500',
  neutral: 'border-white/10 bg-white/5 text-muted',
};

export const themeToneTextClasses: Record<ThemeTone, string> = {
  success: 'text-success-500',
  warning: 'text-warning-500',
  danger: 'text-error-500',
  info: 'text-info-500',
  neutral: 'text-muted',
};

export const themeButtonBase = 'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border-[0.5px] font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon/60 disabled:cursor-not-allowed disabled:opacity-40';

export const themeTableClasses = {
  table: 'w-full min-w-full border-collapse text-foreground',
  bodyText: 'text-[13px]',
  head: 'bg-white/[0.03] text-[11px] font-semibold text-muted',
  headerCell: 'px-4 py-3 align-middle',
  body: 'divide-y divide-white/5',
  row: 'transition-colors hover:bg-white/[0.03]',
  cell: 'px-4 py-3 align-middle',
} as const;

export function themeControlDimensions(size: ThemeControlSize, iconOnly = false): string {
  if (iconOnly) return size === 'xs' ? 'h-7 w-7 px-0 text-[10px] leading-none' : size === 'sm' ? 'h-8 w-8 px-0 text-[11px] leading-none' : size === 'md' ? 'h-9 w-9 px-0 text-xs leading-none' : 'h-11 w-11 px-0 text-sm leading-none';
  return size === 'xs' ? 'h-7 px-2 text-[10px] leading-none' : size === 'sm' ? 'h-8 px-3 text-[11px] leading-none' : size === 'md' ? 'h-9 px-3 text-xs leading-none' : 'h-11 px-4 text-sm leading-none';
}

export function getThemeToggleClassName(selected: boolean | undefined, size: ThemeControlSize, segment?: ThemeSegment, iconOnly = false): string {
  const shape = segment === 'first' ? 'rounded-l-md rounded-r-none' : segment === 'last' ? 'rounded-l-none rounded-r-md' : segment ? 'rounded-none' : '';
  const state = segment
    ? selected ? 'border-0 bg-neon/15 text-neon' : 'border-0 bg-surface-4 text-muted hover:bg-white/[0.06] hover:text-white'
    : selected ? 'border-neon/20 bg-neon/15 text-neon' : 'border-white/[0.08] bg-white/[0.025] text-muted hover:border-white/[0.12] hover:bg-white/[0.06] hover:text-white';
  return `${themeButtonBase} ${themeControlDimensions(size, iconOnly)}${shape ? ` ${shape}` : ''} ${state}`;
}

/** Unknown totals use the API's hasNext signal, never an invented final page. */
export function getThemePaginationState(currentPage: number, totalPages?: number, hasNext = false) {
  const total = totalPages !== undefined && Number.isFinite(totalPages) ? Math.max(1, Math.trunc(totalPages)) : undefined;
  const requested = Number.isFinite(currentPage) ? Math.max(1, Math.trunc(currentPage)) : 1;
  const page = total === undefined ? requested : Math.min(requested, total);
  const start = total === undefined ? page : Math.max(1, Math.min(page - 2, total - 4));
  return {
    page,
    total,
    canPrevious: page > 1,
    canNext: total === undefined ? hasNext : page < total,
    pages: total === undefined ? [page] : Array.from({ length: Math.min(5, total) }, (_, index) => start + index),
  };
}

export function getLogLevelTone(level: string): ThemeTone {
  if (level === 'WARNING') return 'warning';
  if (level === 'ERROR' || level === 'CRITICAL') return 'danger';
  if (level === 'INFO') return 'info';
  return 'neutral';
}
